<script setup lang="ts">
import Button from 'primevue/button'
import { reactive } from 'vue'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import PageHeader from '@/components/app/PageHeader.vue'
import MapCanvas from '@/components/start/MapCanvas.vue'
import AppIcon from '@/components/ui/AppIcon.vue'
import FloatField from '@/components/ui/FloatField.vue'
import type { LngLat } from '@/composables/useLocation'

const props = withDefaults(defineProps<Props>(), {
  initial: () => ({}),
  title: '',
  cta: '',
  deletable: false,
  saving: false,
})

const emit = defineEmits<{
  save: [details: Record<string, string>]
  back: []
  remove: []
  editPoint: []
}>()

const { t } = useI18n()

/* Пусто — берём значения по умолчанию из переводов, а не из захардкоженных строк */
const heading = computed(() => props.title || t('address.new'))
const ctaLabel = computed(() => props.cta || t('address.save'))

/**
 * Детали адреса: плашка с точкой, поля квартиры и подъезда, снизу превью
 * карты 358×160 и кнопка сохранения.
 *
 * Точку правят на карте: и плашка с адресом, и превью просят открыть её на
 * весь экран — это делает хозяин экрана, сюда возвращаются уже с новой.
 */
interface Props {
  address: string
  coords: LngLat
  /** Значения при редактировании сохранённого адреса */
  initial?: Record<string, string>
  title?: string
  cta?: string
  /** Показать корзину в шапке — у сохранённого адреса */
  deletable?: boolean
  /** Адрес уходит на бэкенд — кнопка ждёт ответа */
  saving?: boolean
}

const form = reactive({
  label: props.initial.label ?? '',
  entrance: props.initial.entrance ?? '',
  floor: props.initial.floor ?? '',
  flat: props.initial.flat ?? '',
  comment: props.initial.comment ?? '',
})

/**
 * Обязательных полей в форме нет: дом приходит из разбора адреса Яндексом и
 * уходит в заказ отдельным полем, которого на экране нет, а квартиру есть
 * кому не вводить — в частном доме её просто не существует.
 */

/** Наружу уходят только заполненные поля — пустые в адресе не нужны. */
function save(): void {
  const filled = Object.entries(form).filter(([, value]) => value.trim())
  emit('save', Object.fromEntries(filled.map(([key, value]) => [key, value.trim()])))
}
</script>

<template>
  <section class="start-address">
    <PageHeader :title="heading" custom-back @back="emit('back')">
      <template v-if="deletable" #action>
        <button
          type="button"
          class="start-address__remove"
          :aria-label="t('address.remove')"
          @click="emit('remove')"
        >
          <AppIcon name="trash" :size="24" />
        </button>
      </template>
    </PageHeader>

    <div class="start-address__block">
      <h2 class="start-address__title">{{ t('address.fallbackLabel') }}</h2>
      <div class="start-address__fields">
        <button type="button" class="start-address__plaque" @click="emit('editPoint')">
          <span class="start-address__plaque-icon">
            <AppIcon name="row-address" :size="24" />
          </span>
          <span class="start-address__plaque-text font-13-b">{{ address }}</span>
        </button>

        <FloatField v-model="form.label" :label="t('address.name')" />

        <div class="start-address__row">
          <FloatField v-model="form.entrance" :label="t('address.entrance')" />
          <FloatField v-model="form.floor" :label="t('address.floor')" />
          <FloatField v-model="form.flat" :label="t('address.flat')" />
        </div>

        <FloatField v-model="form.comment" :label="t('address.comment')" />
      </div>
    </div>

    <div class="start-address__block">
      <h2 class="start-address__title">{{ t('common.map') }}</h2>
      <!-- Превью не интерактивно: тап уводит на полноэкранную карту -->
      <button type="button" class="start-address__map-button" @click="emit('editPoint')">
        <MapCanvas class="start-address__map" :center="coords" :zoom="17" />
      </button>
    </div>

    <footer class="start-address__footer">
      <Button :label="ctaLabel" :loading="saving" class="start-address__cta" @click="save" />
    </footer>
  </section>
</template>

<style scoped lang="scss">
.start-address {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;

  &__block {
    padding: var(--screen-padding);
    border-radius: var(--radius-xl);
    background: var(--surface-card);
  }

  &__title {
    margin-bottom: 10px;
    font-size: 24px;
    font-weight: var(--fw-bold);
    line-height: 37px;
  }

  &__fields {
    display: flex;
    flex-direction: column;
    gap: var(--screen-padding);
  }

  /* Плашка выбранной точки: 358×50, иконка 42 белым, адрес красным */
  &__plaque {
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    height: 50px;
    padding: 4px;
    border: 0;
    border-radius: var(--radius-xl);
    background: var(--surface-muted);
    text-align: left;
    cursor: pointer;
  }

  &__plaque-icon {
    display: flex;
    flex: 0 0 42px;
    align-items: center;
    justify-content: center;
    height: 42px;
    border-radius: var(--radius-l);
    background: var(--surface-card);
    color: var(--icon-color);
  }

  &__plaque-text {
    line-height: 16px;
    color: var(--primary-500);
  }

  &__row {
    display: flex;
    gap: 10px;

    > * {
      flex: 1;
      min-width: 0;
    }
  }

  &__map-button {
    display: block;
    width: 100%;
    padding: 0;
    border: 0;
    background: none;
    cursor: pointer;
  }

  &__map {
    height: 160px;
    border-radius: var(--radius-xl);
    /* Карта под кнопкой — жесты забирает родитель */
    pointer-events: none;
  }

  &__remove {
    display: flex;
    padding: 0;
    border: 0;
    background: none;
    color: var(--primary-500);
    cursor: pointer;
  }

  &__footer {
    position: sticky;
    bottom: 0;
    margin-top: auto;
    padding: var(--screen-padding);
    padding-bottom: calc(var(--screen-padding) + var(--tg-safe-area-bottom));
    border-radius: var(--radius-xl) var(--radius-xl) 0 0;
    background: var(--surface-card);
  }

  &__cta {
    width: 100%;
  }
}
</style>
